import qs from 'qs';
import configure, { apiUrls } from '@/constant/configure';
import api from '@/helpers/api_helper';
import { groupSalesQueryFilter } from '@/utils/queryFilters';
import { AddressInfo } from '@/store/modules/liveScheduleStore';

const baseUrl = apiUrls.matching;

export const getContractList = async (userId: string, queryParams?: Record<string, any>) => {
    try {
        const offset = queryParams?.offset || 0;
        const limit = queryParams?.limit || 15;

        queryParams = {
            offset: offset,
            limit: limit,
            ...queryParams,
        };

        const queryStr = qs.stringify(queryParams);

        return await api
            .get(`/seller/${userId}/contract?${queryStr}`, {
                baseURL: baseUrl,
            })
            .then((r) => {
                return {
                    list: r?.data?.items || [],
                    total: r?.data?.totalCount || 0,
                };
            });
    } catch (e: any) {
        if (typeof e === 'string') {
            throw {
                message: e,
            };
        } else {
            throw e;
        }
    }
};

export const getContractOrders = async (userId: string, contractId: string, queryParams: Record<string, any>) => {
    try {
        const offset = queryParams?.offset || 0;
        const limit = queryParams?.limit || 15;

        queryParams = {
            offset: offset,
            limit: limit,
            ...queryParams,
        };

        const queryStr = qs.stringify(queryParams);

        return await api
            .get(`/seller/${userId}/contract/${contractId}/order?${queryStr}`, {
                baseURL: baseUrl,
            })
            .then((r) => {
                return {
                    list: r.data?.items || [],
                    total: r.data?.totalCount || 0,
                };
            });
    } catch (e) {
        if (typeof e === 'string') {
            throw {
                message: e,
            };
        } else {
            throw e;
        }
    }
};

export type ContractProductInfoData = {
    productGroupId: string;
    productName: string;
    representImageUrl: string;
    productImageUrl: string[];
    description: string;
    tags: string[];
    isPublic: boolean;
    isForSale: boolean;
};
export const getContractProductInfo = async (userId: string, contractId: string): Promise<ContractProductInfoData> => {
    try {
        return await api
            .get(`/seller/${userId}/contract/${contractId}/matching-product`, {
                baseURL: baseUrl,
            })
            .then((r) => {
                return r?.data?.item || r?.data;
            });
    } catch (e) {
        if (typeof e === 'string') {
            throw {
                message: e,
            };
        } else {
            throw e;
        }
    }
};

export type PatchContractProductInfoPayload = {
    productName?: string;
    representImageUrl?: string;
    productImageUrl?: string[];
    description?: string;
    tags?: string[];
    isPublic?: boolean;
    isForSale?: boolean;
};
export const patchContractProductInfo = async (
    userId: string,
    contractId: string,
    payload: PatchContractProductInfoPayload,
) => {
    try {
        await api.patch(`seller/${userId}/contract/${contractId}/matching-product`, payload, {
            baseURL: baseUrl,
        });
    } catch (e) {
        if (typeof e === 'string') {
            throw {
                message: e,
            };
        } else {
            throw e;
        }
    }
};

export const deleteContractSchedule = async (userId: string, contractId: string) => {
    try {
        await api.delete(`seller/${userId}/contract/${contractId}`, { baseURL: baseUrl });
    } catch (error) {
        throw error;
    }
};

export type GroupSalesOrdersQueryParams = {
    'search-key'?:
        | 'orderNumber'
        | 'ordererName'
        | 'ordererPhoneNumber'
        | 'ordererEmail'
        | 'recipientName'
        | 'recipientPhoneNumber';
    'search-value'?: string;
    'start-date'?: string; // ex) 2023-06-01
    'end-date'?: string; // ex) 2023-06-08
    offset?: number;
    limit?: number;
} & Record<string, any>;

/**
 * @deprecated
 * @param userId
 * @param queryParams
 * @returns
 */
export const getGroupSalesOrders = async (userId: string, queryParams?: GroupSalesOrdersQueryParams) => {
    try {
        const offset = queryParams?.offset || 0;
        const limit = queryParams?.limit || 15;

        queryParams = {
            offset: offset,
            limit: limit,
            ...queryParams,
        };

        const queryStr = qs.stringify(queryParams);

        return await api
            .get(`/seller/${userId}/matching-product/order?${queryStr}`, {
                baseURL: baseUrl,
            })
            .then((r) => {
                return {
                    list: r?.data?.items || [],
                    total: r?.data?.totalCount || 0,
                };
            });
    } catch (e) {
        if (typeof e === 'string') {
            throw {
                message: e,
            };
        } else {
            throw e;
        }
    }
};

export interface GroupSalesOrdersQueryParams2 {
    offset?: number;
    limit?: number;
}
/**
 *
 * @param userId
 * @param queryParams
 */
export const getGroupSalesOrdersByGroup = async (userId: string, queryParams?: GroupSalesOrdersQueryParams2) => {
    try {
        const queryStr = await groupSalesQueryFilter(queryParams);
        console.log('queryStr:::queryStr:::', queryStr);
        return await api
            .get(`/seller/${userId}/matching-product/order-group?${queryStr}`, { baseURL: baseUrl })
            .then((r) => {
                return {
                    list: r?.data?.items || [],
                    total: r?.data?.total || 0,
                };
            });
    } catch (e) {
        if (typeof e === 'string') {
            throw {
                message: e,
            };
        } else {
            throw e;
        }
    }
};

export const requestCelebritySwag = async (
    sellerId: string,
    contractId: string,
    payload: { receiverInfo?: string } = { receiverInfo: '' },
) => {
    if (!sellerId || !contractId) return;
    try {
        await api.post(`/seller/${sellerId}/contract/${contractId}/sponsorship`, payload, {
            baseURL: baseUrl,
        });
    } catch (e: any) {
        if (typeof e === 'string') {
            throw {
                message: e,
            };
        } else {
            throw e;
        }
    }
};

export const getCelebritySwagCondition = async (
    sellerId: string,
    contractId: string,
): Promise<{
    condition: string;
    isFree: boolean;
    placeHolder: string;
}> => {
    if (!sellerId || !contractId)
        throw {
            data: {
                placeHolder: '필수 데이터인 셀러아이디 또는 컨트랙트 아이디가 누락됨',
            },
        };

    try {
        return await api
            .get(`/seller/${sellerId}/contract/${contractId}/sponsorship-condition`, {
                baseURL: baseUrl,
            })
            .then((r) => {
                return r.data?.item;
            });
    } catch (e: any) {
        if (typeof e === 'string') {
            throw {
                message: e,
            };
        } else {
            throw e;
        }
    }
};

export const getCelebritySwagSponsorship = async (
    sellerId: string,
    sponsorshipId: string,
): Promise<
    | {
          sponsorshipId: string;
          condition: string;
          receiverInfo: string;
          sponsorshipStatus: 'WAIT' | 'REJECTED' | 'APPROVED' | 'PROVIDED' | 'RETRIEVED';
          memo: string;
          message: string;
          isFree: boolean;
          contractId: string;
          sponsorshipInstruction: {
              carriersId: string;
              carriersName: string;
              caution: string;
              invoiceNumber: string;
              managerName: string;
              managerPhoneNumber: string;
              productName: string;
              refundAddress: string;
              shippingDate: string;
              shippingMethod: string;
              usage: string;
              promotion: string;
          };
      }
    | undefined
> => {
    if (!sellerId || !sponsorshipId) return;

    try {
        return await api
            .get(`/seller/${sellerId}/sponsorship/${sponsorshipId}`, {
                baseURL: baseUrl,
            })
            .then((r) => {
                return r.data?.item;
            });
    } catch (e: any) {
        if (typeof e === 'string') {
            throw {
                message: e,
            };
        } else {
            throw e;
        }
    }
};

export type PatchContractDataParams = {
    suggestionOpeningDateTime?: string;
    suggestionClosingDateTime?: string;
};
export const patchContractData = async (sellerId: string, contractId: string, data: PatchContractDataParams) => {
    try {
        await api
            .patch(
                `/seller/${sellerId}/contract/${contractId}`,
                {
                    ...data,
                },
                { baseURL: apiUrls.matching },
            )
            .then((r) => r);
    } catch (e: any) {
        if (typeof e === 'string') {
            throw {
                message: e,
            };
        } else {
            throw e;
        }
    }
};

export const getSellerInfo = async (sellerId: string) => {
    try {
        return await api.get(`/seller/${sellerId}`, { baseURL: apiUrls.matching }).then((r) => r.data.item);
    } catch (e: any) {
        if (typeof e === 'string') {
            throw {
                message: e,
            };
        } else {
            throw e;
        }
    }
};

export const getVendorProfile = async (targetId: string) => {
    const filterQuery = `filter=id%7C%7C%24contL%7C%7C${targetId || ''}`;
    try {
        return await api
            .get(`/common/search/vendors?${filterQuery}`, { baseURL: apiUrls.matching })
            .then((r) => r.data);
    } catch (error) {
        throw error;
    }
};

export default {
    getContractList,
};

export type ScheduleResponse = {
    id: string;
    userId: string;
    blockId?: string;
    scheduleAt: string;
    place?: string;
    updatedAt: string;
    createdAt: string;
    myProductPrice?: Record<string, number>;
    addressInfo?: AddressInfo;
    ddmLiveScheduleProductMappers: {
        id: string;
        productGroup: {
            id: string;
            groupTitle: string;
            representImageUrl: string;
            vendor: {
                id: string;
                nickname: string;
                vendorBusinessInfo: {
                    id: string;
                    storeLocation: string | null;
                    storeName: string | null;
                };
            };
            matchingProducts: {
                id: string;
                optionName: string;
                inventory: number;
                productCharge: number;
                supplyPrice: number;
            }[];
        };
    }[];
    ddmStockRequests: {
        id: string;
        productId: string;
        status: string;
        isPreOrderAvailable: null;
        preOrderDays: null;
    }[];
    ddmSampleRequests: DdmSampleRequests[];
};

export type DdmSampleRequests = {
    id: string;
    status: string;
    recipientName: string;
    phone: string;
    email: string;
    postCode: string;
    address: string;
    addressDetail: string;
    requestNote: string;
    depositImageUrl: null;
    cardFee: number | null;
    surtax: number | null;
    collectingCost: number | null;
    shippingCost: number | null;
    totalCharge: number | null;
    transactedAt: string | null;
    orderNumber: string;
    refundAt: string | null;
    paymentMethod: string | null;
    updatedAt: string;
    createdAt: string;
    matchingProducts: {
        id: string;
        optionName: string;
        inventory: number;
        productCharge: number;
        supplyPrice: number;
        productGroup: {
            id: string;
            groupTitle: string;
            representImageUrl: string;
        };
    }[];
};

export const getDDMLiveSchedule = async ({ userId, query }: { userId: string; query?: string }) => {
    try {
        const { data } = await api.get(`/seller/${userId}/ddm-live-schedule?${query || ''}`, {
            baseURL: apiUrls.matching,
        });
        return (data?.data || []) as ScheduleResponse[];
    } catch (e: any) {
        throw e;
    }
};
export const getDDMLiveScheduleOne = async ({ userId, scheduleId }: { userId: string; scheduleId: string }) => {
    try {
        const { data } = await api.get(`/seller/${userId}/ddm-live-schedule/${scheduleId}`, {
            baseURL: apiUrls.matching,
        });
        return data as ScheduleResponse;
    } catch (e: any) {
        throw e;
    }
};

export const patchDDMLiveSchedule = async ({
    userId,
    scheduleId,
    scheduleAt,
    place,
    blockId,
    myProductPrice,
    addressInfo,
}: {
    userId: string;
    scheduleId: string;
    scheduleAt: string;
    place?: string;
    blockId?: string;
    myProductPrice?: Record<string, number>;
    addressInfo?: any;
}) => {
    try {
        await api.patch(
            `/seller/${userId}/ddm-live-schedule/${scheduleId}`,
            {
                scheduleAt,
                blockId,
                place,
                myProductPrice,
                addressInfo,
            },
            {
                baseURL: apiUrls.matching,
            },
        );
    } catch (e: any) {
        throw e;
    }
};

export const postDDMLiveSchedule = async ({ userId, scheduleAt }: { userId: string; scheduleAt?: string }) => {
    try {
        const { data } = await api.post(
            `/seller/${userId}/ddm-live-schedule`,
            {
                scheduleAt,
            },
            {
                baseURL: apiUrls.matching,
            },
        );
        return data as {
            id: string;
            userId: string;
            blockId: null;
            scheduleAt: null;
            updatedAt: string;
            createdAt: string;
            ddmLiveScheduleProductMappers: [];
        };
    } catch (e: any) {
        throw e;
    }
};

export const deleteDDMLiveSchedule = async ({ userId, scheduleId }: { userId: string; scheduleId: string }) => {
    try {
        await api.delete(`/seller/${userId}/ddm-live-schedule/${scheduleId}`, {
            baseURL: apiUrls.matching,
        });
    } catch (e: any) {
        throw e;
    }
};

export const postDDMLiveScheduleProducts = async ({
    userId,
    scheduleId,
    productGroupId,
}: {
    userId: string;
    scheduleId: string;
    productGroupId: string;
}) => {
    try {
        await api.post(
            `/seller/${userId}/ddm-live-schedule/${scheduleId}/products`,
            {
                productGroupId,
            },
            {
                baseURL: apiUrls.matching,
            },
        );
    } catch (e: any) {
        throw e;
    }
};

export const deleteDDMLiveScheduleProduct = async ({
    userId,
    scheduleId,
    mapperId,
}: {
    userId: string;
    scheduleId: string;
    mapperId: string;
}) => {
    try {
        await api.delete(`/seller/${userId}/ddm-live-schedule/${scheduleId}/products/${mapperId}`, {
            baseURL: apiUrls.matching,
        });
    } catch (e: any) {
        throw e;
    }
};

export const getDDMBuildingList = async () => {
    try {
        const { data } = await api.get<RegionBuildings>(`/common/ddm-building`, {
            baseURL: apiUrls.matching,
        });
        const map = new Map();
        Object.values(data).forEach((buildings) => {
            buildings.forEach((building) => {
                const buildingFloorsSet = new Map();
                building.buildingFloors.forEach((floor) => {
                    buildingFloorsSet.set(floor.floor, floor);
                });
                map.set(building.buildingName, { ...building, buildingFloorsSet });
            });
        });
        return map as Map<string, Building & { buildingFloorsSet: Map<string, Floor> }>;
    } catch (e: any) {
        throw e;
    }
};

export const postStockRequests = async ({
    sellerId,
    scheduleId,
    productIds,
}: {
    sellerId: string;
    scheduleId: string;
    productIds: string[];
}) => {
    try {
        const { data } = await api.post<{
            data: {
                scheduleId: string;
                productId: string;
                status: string;
                isPreOrderAvailable: null;
                preOrderDays: null;
                createdAt: string;
                updatedAt: string;
                id: string;
            }[];
            count: number;
        }>(
            `/seller/${sellerId}/ddm-live-schedule/${scheduleId}/stock-requests`,
            {
                productIds,
            },
            {
                baseURL: apiUrls.matching,
            },
        );
        return data?.data;
    } catch (e: any) {
        throw e;
    }
};

export const postSampleStockRequests = async ({
    sellerId,
    scheduleId,
    productIds,
    recipientName,
    phone,
    email,
    postCode,
    address,
    addressDetail,
    requestNote,
}: {
    sellerId: string;
    scheduleId: string;
    productIds: string[];
    recipientName: string;
    phone: string;
    email: string;
    postCode: string;
    address: string;
    addressDetail: string;
    requestNote: string;
}) => {
    try {
        const { data } = await api.post<{
            data: {
                status: string;
                recipientName: string;
                phone: string;
                email: string;
                address: string;
                addressDetail: string;
                requestNote: string;
                schedule: {
                    id: string;
                };
                matchingProducts: {
                    id: string;
                    optionName: string;
                    inventory: number;
                    supplyPrice: number;
                    marketPrice: number;
                    discountMethod: string;
                    discountValue: number;
                    productCharge: number;
                    isPublic: string;
                    updatedAt: string;
                    createdAt: string;
                    isActivated: string;
                    marginRate: number;
                    productCode: null;
                    optionImageUrl: null;
                }[];
                depositImageUrl: null;
                updatedAt: string;
                createdAt: string;
                id: string;
            };
        }>(
            `/seller/${sellerId}/ddm-live-schedule/${scheduleId}/sample-requests`,
            {
                productIds,
                recipientName,
                phone,
                email,
                postCode,
                address,
                addressDetail,
                requestNote,
            },
            {
                baseURL: apiUrls.matching,
            },
        );
        return data?.data;
    } catch (e: any) {
        throw e;
    }
};

export const patchSampleRequestDepositImage = async ({
    sellerId,
    scheduleId,
    sampleRequestId,
    depositImageUrls,
}: {
    sellerId: string;
    scheduleId: string;
    sampleRequestId: string;
    depositImageUrls: string[];
}) => {
    try {
        const { data } = await api.patch<{
            data: {
                id: string;
                status: string;
                depositImageUrl: string;
            };
        }>(
            `/seller/${sellerId}/ddm-live-schedule/${scheduleId}/sample-requests/${sampleRequestId}/deposit-images`,
            {
                depositImageUrl: depositImageUrls,
            },
            {
                baseURL: apiUrls.matching,
            },
        );
        return data?.data;
    } catch (e: any) {
        throw e;
    }
};

export const getSampleRequestDetail = async ({
    sellerId,
    sampleRequestId,
}: {
    sellerId: string;
    sampleRequestId: string;
}) => {
    try {
        const { data } = await api.get<DdmSampleRequests>(
            `/seller/${sellerId}/ddm-live-schedule/sample-requests/${sampleRequestId}`,
            {
                baseURL: apiUrls.matching,
            },
        );
        return data;
    } catch (e: any) {
        throw e;
    }
};

export const postSampleRequestPaymentAmount = async ({
    sellerId,
    scheduleId,
    productIds,
}: {
    sellerId: string;
    scheduleId: string;
    productIds: string[];
}) => {
    try {
        const { data } = await api.post<{
            cardFee: number;
            surtax: number;
            collectingCost: number;
            shippingCost: number;
        }>(
            `/seller/${sellerId}/ddm-live-schedule/${scheduleId}/sample-requests/payment-amount`,
            {
                productIds,
            },
            {
                baseURL: apiUrls.matching,
            },
        );
        return data;
    } catch (e: any) {
        throw e;
    }
};

type RegionBuildings = {
    동대문: Building[];
    남대문: Building[];
    부산: Building[];
};
type Building = {
    id: string;
    location: string;
    buildingName: string;
    buildingFloors: Floor[];
};

type Floor = {
    floor: string;
    guide_image: string;
};

// 소도몰 셀러 공구상품 관리 API

/**
 * 소도몰 공구상품 리스트 조회
 */
export const getSodomallContractList = async (sellerId: string, queryParams?: Record<string, any>) => {
    try {
        const offset = queryParams?.offset || 0;
        const limit = queryParams?.limit || 15;

        const params = {
            offset,
            limit,
            ...queryParams,
        };

        const queryStr = qs.stringify(params);

        return await api
            .get(`/seller/${sellerId}/sodomall/contracts?${queryStr}`, {
                baseURL: baseUrl,
            })
            .then((r) => {
                return {
                    list: r?.data?.items || [],
                    total: r?.data?.totalCount || 0,
                };
            });
    } catch (e: any) {
        throw e;
    }
};

/**
 * 소도몰 공구상품 옵션별 재고 조회
 */
export const getSodomallContractInventory = async (sellerId: string, contractId: string) => {
    try {
        const { data } = await api.get(`/seller/${sellerId}/sodomall/contracts/${contractId}/inventory`, {
            baseURL: baseUrl,
        });
        return data;
    } catch (e: any) {
        throw e;
    }
};

/**
 * 소도몰 공구상품 옵션별 재고 수정
 */
export const updateSodomallContractInventory = async (
    sellerId: string,
    contractId: string,
    inventoryUpdates: { mallProductId: string; inventory: number }[],
) => {
    try {
        const { data } = await api.patch(
            `/seller/${sellerId}/sodomall/contracts/${contractId}/inventory`,
            { inventoryUpdates },
            {
                baseURL: baseUrl,
            },
        );
        return data;
    } catch (e: any) {
        throw e;
    }
};

/**
 * 소도몰 공구상품 픽업일/입고일 수정
 */
export const updateSodomallContractSchedule = async (
    sellerId: string,
    contractId: string,
    scheduleData: {
        receivingDate?: string | null;
        pickupDateStart?: string | null;
        pickupDateEnd?: string | null;
    },
) => {
    try {
        const { data } = await api.patch(
            `/seller/${sellerId}/sodomall/contracts/${contractId}/schedule`,
            scheduleData,
            {
                baseURL: baseUrl,
            },
        );
        return data;
    } catch (e: any) {
        throw e;
    }
};

/**
 * 소도몰 픽업 확인 (주문 상태를 주문확인완료로 변경)
 */
export const confirmSodomallPickup = async (
    sellerId: string,
    orderId: string,
): Promise<{
    success: boolean;
    orderId: string;
    orderState: string;
    orderStateCode: number;
}> => {
    try {
        const { data } = await api.post(
            `/seller/${sellerId}/sodomall/contracts/orders/${orderId}/pickup-confirm`,
            {},
            {
                baseURL: baseUrl,
            },
        );
        return data;
    } catch (e: any) {
        throw e;
    }
};

/**
 * 소도몰 픽업완료 취소 (주문확인완료 → 결제완료)
 */
export const cancelSodomallPickup = async (
    sellerId: string,
    orderId: string,
): Promise<{
    success: boolean;
    orderId: string;
    orderState: string;
    orderStateCode: number;
}> => {
    try {
        const { data } = await api.post(
            `/seller/${sellerId}/sodomall/contracts/orders/${orderId}/cancel-pickup`,
            {},
            {
                baseURL: baseUrl,
            },
        );
        return data;
    } catch (e: any) {
        throw e;
    }
};

/**
 * 소도몰 일괄 주문 상태 변경 (통합)
 */
export const bulkUpdateSodomallOrderState = async (
    sellerId: string,
    orderIds: string[],
    stateType: 'pickup-complete' | 'reservation-complete' | 'prepayment' | 'noshow' | 'reject',
): Promise<{
    success: boolean;
    successCount: number;
    failCount: number;
    results: { orderId: string; success: boolean; error?: string }[];
}> => {
    try {
        const { data } = await api.post(
            `/seller/${sellerId}/sodomall/contracts/orders/bulk-update-state`,
            { orderIds, stateType },
            {
                baseURL: baseUrl,
            },
        );
        return data;
    } catch (e: any) {
        throw e;
    }
};

/**
 * @deprecated 대신 bulkUpdateSodomallOrderState 사용
 */
export const confirmSodomallBulkPickup = async (
    sellerId: string,
    orderIds: string[],
): Promise<{
    success: boolean;
    successCount: number;
    failCount: number;
    results: { orderId: string; success: boolean; error?: string }[];
}> => {
    return bulkUpdateSodomallOrderState(sellerId, orderIds, 'pickup-complete');
};

/**
 * 소도몰 노쇼 상태로 변경
 */
export const confirmSodomallNoshow = async (
    sellerId: string,
    orderId: string,
): Promise<{
    success: boolean;
    orderId: string;
    orderState: string;
    orderStateCode: number;
}> => {
    try {
        const { data } = await api.post(
            `/seller/${sellerId}/sodomall/contracts/orders/${orderId}/noshow`,
            {},
            {
                baseURL: baseUrl,
            },
        );
        return data;
    } catch (e: any) {
        throw e;
    }
};

/**
 * 소도몰 거부 상태로 변경
 */
export const confirmSodomallReject = async (
    sellerId: string,
    orderId: string,
): Promise<{
    success: boolean;
    orderId: string;
    orderState: string;
    orderStateCode: number;
}> => {
    try {
        const { data } = await api.post(
            `/seller/${sellerId}/sodomall/contracts/orders/${orderId}/reject`,
            {},
            {
                baseURL: baseUrl,
            },
        );
        return data;
    } catch (e: any) {
        throw e;
    }
};

/**
 * 소도몰 선결제 확인
 */
export const confirmSodomallPrepayment = async (
    sellerId: string,
    orderId: string,
): Promise<{
    success: boolean;
    orderId: string;
    orderState: string;
    orderStateCode: number;
}> => {
    try {
        const { data } = await api.post(
            `/seller/${sellerId}/sodomall/contracts/orders/${orderId}/prepayment`,
            {},
            {
                baseURL: baseUrl,
            },
        );
        return data;
    } catch (e: any) {
        throw e;
    }
};

// 소도몰 공구주문 현황 조회 API (전용 - CRUD 미사용)

export interface SodomallOrderSearchParams {
    offset?: number;
    limit?: number;
    ordererName?: string;
    phoneNumber?: string;
    productName?: string;
    orderStateTypes?: string[];
    startDate?: string;
    endDate?: string;
}

// 상품별 그룹화된 주문 항목 (API 응답 구조)
export interface SodomallOrderProductGroup {
    productName: string; // 상품명
    options: {
        mallOrderId: string;
        mallOrderNumber: string;
        orderStateCode: number;
        orderState: string;
        productInfo: {
            representProductName: string;
            productOptionsName: string;
            productAmount: number;
            totalCharge: number;
        };
        receivingDate?: string | null;
        pickupDateStart?: string | null;
        pickupDateEnd?: string | null;
    }[];
}

export interface SodomallOrderItem {
    mallOrderGroupId: string;
    mallOrderGroupNumber: string;
    orderedAt: Date;
    ordererInfo: {
        ordererName: string;
        ordererPhoneNumber: string;
    };
    products: SodomallOrderProductGroup[]; // 상품별 그룹화된 배열
    priceInfo: {
        totalCharge: number;
    };
    // 하위 호환성을 위한 필드 (기존 코드와의 호환)
    mallSubOrderList?: {
        mallOrderId: string;
        mallOrderNumber: string;
        orderStateCode: number;
        orderState: string;
        productInfo: {
            representProductName: string;
            productOptionsName: string;
            productAmount: number;
            totalCharge: number;
        };
        receivingDate?: string | null;
        pickupDateStart?: string | null;
        pickupDateEnd?: string | null;
    }[];
}

export interface SodomallOrderListResponse {
    items: SodomallOrderItem[];
    total: number;
}

/**
 * 소도몰 공구주문 현황 조회 (전용 API - CRUD 미사용, TypeORM 직접 조회)
 */
export const getSodomallOrderList = async (
    sellerId: string,
    params: SodomallOrderSearchParams,
): Promise<{ list: SodomallOrderItem[]; total: number }> => {
    try {
        const queryParams: Record<string, any> = {
            offset: params.offset || 0,
            limit: params.limit || 15,
        };

        if (params.ordererName) queryParams.ordererName = params.ordererName;
        if (params.phoneNumber) queryParams.phoneNumber = params.phoneNumber;
        if (params.productName) queryParams.productName = params.productName;
        if (params.orderStateTypes && params.orderStateTypes.length > 0) {
            queryParams.orderStateTypes = params.orderStateTypes.join(',');
        }
        if (params.startDate) queryParams.startDate = params.startDate;
        if (params.endDate) queryParams.endDate = params.endDate;

        const queryStr = qs.stringify(queryParams);

        const { data } = await api.get<SodomallOrderListResponse>(
            `/seller/${sellerId}/sodomall/contracts/orders?${queryStr}`,
            {
                baseURL: baseUrl,
            },
        );

        return {
            list: data?.items || [],
            total: data?.total || 0,
        };
    } catch (e: any) {
        throw e;
    }
};

// 소도몰 본사 상품 조회 API

export interface VendorProductOption {
    productId: string;
    optionName: string;
    productCharge: number;
    inventory: number;
    supplyPrice?: number | null;
    marginRate?: number | null;
}

export interface VendorProductItem {
    productGroupId: string;
    productName: string;
    productImage: string | null;
    productImages: string[];
    options: VendorProductOption[];
}

export interface VendorProductListResponse {
    items: VendorProductItem[];
    total: number;
}

/**
 * 본사 상품 목록 조회 (소도몰 역할의 vendorUser 상품)
 */
export const getVendorProductList = async (
    sellerId: string,
    params: {
        offset?: number;
        limit?: number;
        productName?: string;
    },
): Promise<{ list: VendorProductItem[]; total: number }> => {
    try {
        const queryParams: Record<string, any> = {
            offset: params.offset || 0,
            limit: params.limit || 15,
        };

        if (params.productName) queryParams.productName = params.productName;

        const queryStr = qs.stringify(queryParams);

        const { data } = await api.get<VendorProductListResponse>(
            `/seller/${sellerId}/sodomall/contracts/vendor-products?${queryStr}`,
            {
                baseURL: baseUrl,
            },
        );

        return {
            list: data?.items || [],
            total: data?.total || 0,
        };
    } catch (e: any) {
        throw e;
    }
};

// 소도몰 공구 생성 API

export interface CreateSodomallContractParams {
    productGroupId: string;
    suggestionOpeningDateTime: string;
    suggestionClosingDateTime: string;
    options: {
        productId: string;
        inventory: number;
    }[];
    receivingDate?: string;
    pickupDateStart?: string;
    pickupDateEnd?: string;
}

export interface CreateSodomallContractResponse {
    success: boolean;
    contractId: string;
    mallProductGroupId: string;
}

/**
 * 소도몰 공구 생성
 */
export const createSodomallContract = async (
    sellerId: string,
    params: CreateSodomallContractParams,
): Promise<CreateSodomallContractResponse> => {
    try {
        const { data } = await api.post<CreateSodomallContractResponse>(
            `/seller/${sellerId}/sodomall/contracts/create`,
            params,
            {
                baseURL: baseUrl,
            },
        );

        return data;
    } catch (e: any) {
        throw e;
    }
};
